import { baseApi } from '@store/baseApi';
import { enhancedApi as generatedUsersApi } from '@store/generated/users';

/**
 * Enhanced generated users API with tags for cross-store invalidation.
 * - SubscriptionDetails: invalidates subscription queries when subscription data changes
 * - UserInfo: ensures v2 user-info endpoint is refetched alongside the legacy endpoint
 *   when any mutation invalidates UserInfo (e.g. after onboarding completion)
 */
const enhancedGeneratedUsersApi = generatedUsersApi.enhanceEndpoints({
  addTagTypes: ['SubscriptionDetails', 'UserInfo'],
  endpoints: {
    getV2ProviderSubscriptionDetails: {
      providesTags: ['SubscriptionDetails'],
    },
    getV2ProviderUserInfo: {
      providesTags: ['UserInfo'],
    },
  },
});

import type { ProviderBillingInfo } from '../../../types/types';

import type {
  AddTroubleWordsRequest,
  GetSpecialtiesResponse,
  GetTemplatesResponse,
  GetTroubleWordsResponse,
  GetTutorialProgressResponse,
  GetUserInfoResponse,
  LanguagesAndPreferencesResponse,
  UpdateTutorialProgressRequest,
} from './types';

/**
 * Feature-specific tag types for cache management
 */
const USER_TAG_TYPES = ['User', 'UserInfo', 'Templates', 'TroubleWords', 'Tutorials'] as const;

/**
 * User Feature API - manages all user-related endpoints
 */
export const userApi = baseApi
  .enhanceEndpoints({
    addTagTypes: USER_TAG_TYPES,
  })
  .injectEndpoints({
    endpoints: builder => ({
      // Get user info
      getUserInfo: builder.query<GetUserInfoResponse, undefined>({
        query: () => '/user/getUserInfo',
        providesTags: ['UserInfo'],
      }),

      // Templates
      getTemplates: builder.query<GetTemplatesResponse, undefined>({
        query: () => '/user/getTemplates',
        providesTags: ['Templates'],
      }),

      copyUserTemplate: builder.mutation<unknown, { template_id: string }>({
        query: body => ({
          url: '/template/copyUserTemplate',
          method: 'POST',
          body,
        }),
        invalidatesTags: ['Templates'],
      }),

      copyPublicTemplateToUser: builder.mutation<unknown, { template_id: string }>({
        query: body => ({
          url: '/template/copyPublicTemplateToUser',
          method: 'POST',
          body,
        }),
        invalidatesTags: ['Templates'],
      }),

      // Trouble words
      getTroubleWords: builder.query<GetTroubleWordsResponse, undefined>({
        query: () => '/user/getTroubleWords',
        providesTags: ['TroubleWords'],
      }),

      addTroubleWords: builder.mutation<unknown, AddTroubleWordsRequest>({
        query: body => ({
          url: '/user/addTroubleWords',
          method: 'POST',
          body,
        }),
        invalidatesTags: ['TroubleWords'],
      }),

      // Specialties
      getAvailableSpecialties: builder.query<GetSpecialtiesResponse, undefined>({
        query: () => '/specialties/getAvailableSpecialties',
      }),

      // Languages & preferences
      getLanguagesAndPreferences: builder.query<LanguagesAndPreferencesResponse, undefined>({
        query: () => '/user/getLanguagesAndPreferences',
        providesTags: ['UserInfo'],
      }),

      updateLanguageAndPreferences: builder.mutation<
        unknown,
        {
          languages: string[];
          subject_language: string;
        }
      >({
        query: body => ({
          url: '/user/languageAndPreferencesForm',
          method: 'POST',
          body,
        }),
        invalidatesTags: ['UserInfo'],
      }),

      // Onboarding
      updateOnboardingSteps: builder.mutation<unknown, Record<string, boolean>>({
        query: body => ({
          url: '/onboarding/updateOnboardingSteps',
          method: 'POST',
          body,
        }),
        invalidatesTags: ['UserInfo'],
      }),

      // Login & version tracking
      userLogin: builder.mutation<unknown, { device_info: { platform: string } }>({
        query: body => ({
          url: '/user/userLogin',
          method: 'POST',
          body,
        }),
      }),

      updateLastVersionUsed: builder.mutation<unknown, { platform: string; version: string }>({
        query: body => ({
          url: '/user/updateLastVersionUsed',
          method: 'POST',
          body,
        }),
      }),

      updateDefaultTimezone: builder.mutation<unknown, { timezone: string }>({
        query: body => ({
          url: '/user/updateDefaultTimezone',
          method: 'POST',
          body,
        }),
      }),

      // Tutorials
      getTutorialProgress: builder.query<GetTutorialProgressResponse, undefined>({
        query: () => '/tutorials/getProgress',
        providesTags: ['Tutorials'],
      }),

      updateTutorialProgress: builder.mutation<unknown, UpdateTutorialProgressRequest>({
        query: body => ({
          url: '/tutorials/updateProgress',
          method: 'POST',
          body,
        }),
        invalidatesTags: ['Tutorials'],
      }),

      // Feature flags (v2)
      toggleFeatureFlag: builder.mutation<unknown, { feature_name: string; enable: boolean }>({
        query: body => ({
          url: '/v2/provider/features/toggle-feature-flag',
          method: 'POST',
          body,
        }),
        invalidatesTags: ['UserInfo'],
      }),

      toggleEhrMode: builder.mutation<unknown, { enable: boolean }>({
        query: body => ({
          url: '/v2/provider/features/toggle-ehr-mode',
          method: 'POST',
          body,
        }),
        invalidatesTags: ['UserInfo'],
      }),

      // User profile updates
      updateUserInfo: builder.mutation<
        {
          status: number;
          stripe_customer_id?: string;
          stripe_subscription_id?: string;
        },
        {
          user_info: {
            name: string;
            email: string;
            institution: string;
            specialization: string;
            discovery_channel: string;
            discovery_channel_text: string;
            referral_code: string;
            mobile_phone_num: string;
            checked_sms_consent: boolean;
            checked_sms_product_consent: boolean;
            accepted_tos: boolean;
            firstName?: string;
            lastName?: string;
          };
          brand?: string;
          platform?: string;
          utm?: Record<string, string>;
          signup_params?: Record<string, string>;
          invite_token?: string | null;
        }
      >({
        query: body => ({
          url: '/user/updateUserInfo',
          method: 'POST',
          body,
        }),
        invalidatesTags: ['UserInfo'],
      }),

      addSpecialty: builder.mutation<unknown, { specialty_id: string }>({
        query: body => ({
          url: '/user/addSpecialty',
          method: 'POST',
          body,
        }),
        invalidatesTags: ['UserInfo'],
      }),

      // Referral endpoints
      validateReferralCode: builder.mutation<
        {
          status: number;
          referred_by_user_id?: string;
        },
        { referred_by_code: string }
      >({
        query: body => ({
          url: '/referrals/validateReferralCode',
          method: 'POST',
          body,
        }),
      }),

      updateUserReferralInfo: builder.mutation<
        unknown,
        {
          is_new_user: boolean;
          referred_by_code: string;
          referred_by_user_rewarded: boolean;
          referred_by_user_id: string;
          stripe_customer_id: string;
          stripe_subscription_id: string;
        }
      >({
        query: body => ({
          url: '/referrals/updateUserReferralInfo',
          method: 'POST',
          body,
        }),
      }),

      // Billing endpoints
      createOrRetrieveCheckoutSession: builder.mutation<
        {
          checkout_session_url?: string;
        },
        Record<string, never>
      >({
        query: () => ({
          url: '/user/createOrRetrieveCheckoutSession',
          method: 'POST',
          body: {},
        }),
      }),

      createCustomerPortalSession: builder.mutation<
        {
          portal_session_url?: string;
        },
        Record<string, never>
      >({
        query: () => ({
          url: '/user/createCustomerPortalSession',
          method: 'POST',
          body: {},
        }),
      }),

      // Email verification
      resendVerificationEmail: builder.mutation<unknown, Record<string, never>>({
        query: () => ({
          url: '/user/resendVerificationEmail',
          method: 'POST',
          body: {},
        }),
      }),

      // Admin endpoints
      getAdminEmails: builder.query<
        {
          admin_emails: Array<{
            email_address: string;
            email_id: string;
            name: string;
            verified_status: boolean;
          }>;
        },
        undefined
      >({
        query: () => '/user/getAdminEmails',
      }),

      // Onboarding endpoints
      getOnboardingStatus: builder.query<
        {
          onboarding_status: {
            tasks: Array<{
              task_id: string;
              seq: number;
              title: string;
              description: string | null;
              is_completed: boolean;
              target_value: number | null;
              video_link: string | null;
              navigation_link: string | null;
            }>;
            show_onboarding_tasks: boolean;
            discount_amount?: number;
            apply_discount?: boolean;
            stripe_current_period_end?: number;
          };
        },
        undefined
      >({
        query: () => '/user/getOnboardingStatus',
      }),

      markConsentLinkClicked: builder.mutation<unknown, undefined>({
        query: () => ({
          url: '/onboarding/consentLinkClicked',
          method: 'GET',
        }),
      }),

      // Provider billing information
      getProviderBillingInfo: builder.query<
        {
          provider_billing_info: Partial<ProviderBillingInfo>;
        },
        undefined
      >({
        query: () => '/user/getProviderBillingInfo',
        providesTags: ['UserInfo'],
      }),

      updateProviderBillingInfo: builder.mutation<
        unknown,
        {
          updated_fields: Partial<ProviderBillingInfo>;
        }
      >({
        query: body => ({
          url: '/user/updateProviderBillingInfo',
          method: 'POST',
          body,
        }),
        invalidatesTags: ['UserInfo'],
      }),

      // Signup attribution capture (gdn-1180): persist jp_attribution cookie
      // payload server-side and trigger GA4 MP + Meta CAPI fires that pair with
      // the browser-side pixel via the shared event_id.
      captureSignupAttribution: builder.mutation<
        {
          persisted: boolean;
          ga4_fired: boolean;
          capi_fired: boolean;
          first_capture: boolean;
        },
        {
          cookie_payload: Record<string, unknown> | null;
          event_id: string;
        }
      >({
        query: body => ({
          url: '/v2/provider/signup-attribution',
          method: 'POST',
          body,
        }),
      }),
    }),
    overrideExisting: false,
  });

// Export hooks with user-friendly names
// NOTE: useGetUserInfoQuery and useLazyGetUserInfoQuery are now defined in hooks.ts
// as combined hooks that merge legacy and v2 data
const {
  useGetUserInfoQuery: useGetLegacyUserInfoQuery,
  useLazyGetUserInfoQuery: useLazyGetLegacyUserInfoQuery,
  useGetTemplatesQuery,
  useLazyGetTemplatesQuery,
  useCopyUserTemplateMutation,
  useCopyPublicTemplateToUserMutation,
  useGetTroubleWordsQuery,
  useLazyGetTroubleWordsQuery,
  useAddTroubleWordsMutation,
  useGetAvailableSpecialtiesQuery,
  useLazyGetAvailableSpecialtiesQuery,
  useGetLanguagesAndPreferencesQuery,
  useUpdateLanguageAndPreferencesMutation,
  useUpdateOnboardingStepsMutation,
  useUserLoginMutation,
  useUpdateLastVersionUsedMutation,
  useUpdateDefaultTimezoneMutation,
  useGetTutorialProgressQuery,
  useUpdateTutorialProgressMutation,
  useToggleFeatureFlagMutation,
  useToggleEhrModeMutation,
  useUpdateUserInfoMutation,
  useAddSpecialtyMutation,
  useValidateReferralCodeMutation,
  useUpdateUserReferralInfoMutation,
  useCreateOrRetrieveCheckoutSessionMutation,
  useCreateCustomerPortalSessionMutation,
  useResendVerificationEmailMutation,
  useGetAdminEmailsQuery,
  useLazyGetAdminEmailsQuery,
  useGetOnboardingStatusQuery,
  useLazyGetOnboardingStatusQuery,
  useMarkConsentLinkClickedMutation,
  useGetProviderBillingInfoQuery,
  useLazyGetProviderBillingInfoQuery,
  useUpdateProviderBillingInfoMutation,
  useCaptureSignupAttributionMutation,
} = userApi;

// Export legacy hooks for internal use
export { useGetLegacyUserInfoQuery, useLazyGetLegacyUserInfoQuery };

// Export other hooks
export {
  useAddSpecialtyMutation,
  useAddTroubleWordsMutation,
  useCaptureSignupAttributionMutation,
  useCopyPublicTemplateToUserMutation,
  useCopyUserTemplateMutation,
  useCreateCustomerPortalSessionMutation,
  useCreateOrRetrieveCheckoutSessionMutation,
  useGetAdminEmailsQuery,
  useGetAvailableSpecialtiesQuery,
  useGetLanguagesAndPreferencesQuery,
  useGetOnboardingStatusQuery,
  useGetProviderBillingInfoQuery,
  useGetTemplatesQuery,
  useGetTroubleWordsQuery,
  useGetTutorialProgressQuery,
  useLazyGetAdminEmailsQuery,
  useLazyGetAvailableSpecialtiesQuery,
  useLazyGetOnboardingStatusQuery,
  useLazyGetProviderBillingInfoQuery,
  useLazyGetTemplatesQuery,
  useLazyGetTroubleWordsQuery,
  useMarkConsentLinkClickedMutation,
  useResendVerificationEmailMutation,
  useToggleEhrModeMutation,
  useToggleFeatureFlagMutation,
  useUpdateDefaultTimezoneMutation,
  useUpdateLanguageAndPreferencesMutation,
  useUpdateLastVersionUsedMutation,
  useUpdateOnboardingStepsMutation,
  useUpdateProviderBillingInfoMutation,
  useUpdateTutorialProgressMutation,
  useUpdateUserInfoMutation,
  useUpdateUserReferralInfoMutation,
  useUserLoginMutation,
  useValidateReferralCodeMutation,
};

// Re-export generated RTK Query hooks with user-friendly names
export const {
  useGetV2ProviderMarketingCustomizationsQuery: useGetMarketingCustomizationsQuery,
  useLazyGetV2ProviderMarketingCustomizationsQuery: useLazyGetMarketingCustomizationsQuery,
  useGetV2ProviderSubscriptionDetailsQuery: useSubscriptionDetails,
  useLazyGetV2ProviderSubscriptionDetailsQuery: useLazyGetSubscriptionDetails,
  usePostV2ProviderPhoneVerificationSendMutation: useSendPhoneVerificationCodeMutation,
  usePostV2ProviderUsersMutation: useCreateUserMutation,
} = enhancedGeneratedUsersApi;

// Read-only account context without the legacy user-initialization side effects.
export const { useLazyGetV2ProviderUserInfoQuery: useLazyGetUserAccessQuery } =
  enhancedGeneratedUsersApi;
